import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import FingerprintIcon from '@mui/icons-material/Fingerprint'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../../Context/AuthContext'
import { validateEmail } from '../../Utils/validators'
import MfaModal from './MfaModal'
import ForgotPasswordModal from './ForgotPasswordModal'
import passkeyService from '../../Services/passkeyService'

const getSaved = () => {
  try { return JSON.parse(localStorage.getItem('rememberMe')) || {} } catch { return {} }
}

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
)

const LoginComponent = () => {
  const navigate = useNavigate()
  const { login, loginWithPhone, loginWithPasskey, loginWithMicrosoft } = useAuth()

  const saved = getSaved()

  // 'email' | 'phone' — toggle within the main login form
  const [loginMethod, setLoginMethod] = React.useState('email')

  // Whether to show the separate passkey sign-in view
  const [showPasskeyView, setShowPasskeyView] = React.useState(false)

  // Email fields
  const [email, setEmail] = React.useState(saved.email || '')
  const [password, setPassword] = React.useState(saved.password || '')
  const [rememberMe, setRememberMe] = React.useState(saved.rememberMe || false)
  const [showPassword, setShowPassword] = React.useState(false)
  const [emailErr, setEmailErr] = React.useState('')

  // Phone field
  const [phone, setPhone] = React.useState('')

  // Passkey view field — pre-fill with email if already entered
  const [passkeyEmail, setPasskeyEmail] = React.useState(saved.email || '')
  const [passkeyEmailErr, setPasskeyEmailErr] = React.useState('')

  // Shared
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  // MFA modal
  const [mfaOpen, setMfaOpen] = React.useState(false)
  const [mfaUserId, setMfaUserId] = React.useState('')
  const [mfaType, setMfaType] = React.useState('email')

  // Forgot password modal
  const [forgotOpen, setForgotOpen] = React.useState(false)

  // Autofill detection — browser autofill bypasses React onChange, so MUI label doesn't shrink
  const [emailAutofilled, setEmailAutofilled] = React.useState(false)
  const [passwordAutofilled, setPasswordAutofilled] = React.useState(false)

  const onAutofill = (setter) => (e) => {
    if (e.animationName === 'mui-auto-fill') setter(true)
    if (e.animationName === 'mui-auto-fill-cancel') setter(false)
  }

  const clearError = () => setError('')

  const switchMethod = (method) => {
    setLoginMethod(method)
    clearError()
  }

  const openPasskeyView = () => {
    // Pre-fill with whatever email was typed in the main form
    setPasskeyEmail(email || saved.email || '')
    setPasskeyEmailErr('')
    clearError()
    setShowPasskeyView(true)
  }

  const closePasskeyView = () => {
    setShowPasskeyView(false)
    clearError()
  }

  const persistRememberMe = () => {
    if (rememberMe) {
      localStorage.setItem('rememberMe', JSON.stringify({ email, password, rememberMe: true }))
    } else {
      localStorage.removeItem('rememberMe')
    }
  }

  // ── Email login ────────────────────────────────────────────────────────────

  const handleEmailLogin = async (e) => {
    e.preventDefault()
    const v = validateEmail(email.trim())
    if (v.msg) { setEmailErr(v.msg); return }
    if (!password) { setError('Password is required.'); return }
    setLoading(true); clearError()
    try {
      const result = await login(email.trim(), password, rememberMe)
      if (result.requiresMfa) {
        setMfaUserId(result.userId)
        setMfaType('email')
        setMfaOpen(true)
      } else {
        persistRememberMe()
        toast.success('Signed in successfully.')
        navigate('/home')
      }
    } catch (err) {
      setError(err.message || 'Sign in failed. Check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  // ── Phone login ────────────────────────────────────────────────────────────

  const handlePhoneLogin = async (e) => {
    e.preventDefault()
    if (!phone.trim()) { setError('Enter your phone number.'); return }
    setLoading(true); clearError()
    try {
      const result = await loginWithPhone(phone.trim())
      setMfaUserId(result.userId)
      setMfaType('phone')
      setMfaOpen(true)
    } catch (err) {
      setError(err.message || 'Phone login failed.')
    } finally {
      setLoading(false)
    }
  }

  // ── Passkey login ──────────────────────────────────────────────────────────

  const handlePasskeyLogin = async (e) => {
    e.preventDefault()
    const v = validateEmail(passkeyEmail.trim())
    if (v.msg) { setPasskeyEmailErr(v.msg); return }
    setLoading(true); clearError()
    try {
      await loginWithPasskey(passkeyEmail.trim())
      toast.success('Signed in with passkey.')
      navigate('/home')
    } catch (err) {
      setError(err.message || 'Passkey sign in failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── MFA success ────────────────────────────────────────────────────────────

  const handleMfaSuccess = () => {
    setMfaOpen(false)
    if (loginMethod === 'email') persistRememberMe()
    toast.success('Signed in successfully.')
    navigate('/home')
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Passkey view (replaces main form)
  // ─────────────────────────────────────────────────────────────────────────

  if (showPasskeyView) {
    return (
      <Box className="Login_Page_Box">
        <Box
          className="Login_Box"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 420, p: 3 }}
        >
          <Box sx={{ mb: 1.5 }}>
            <img src="./logo-dark.png" alt="Rentalboard" width="80px" />
          </Box>

          <Typography component="h1" variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
            PASSKEY SIGN IN
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Use your saved passkey (biometric or security key) to sign in.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 1.5 }} onClose={clearError}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasskeyLogin} noValidate sx={{ width: '100%' }}>
            <TextField
              autoFocus
              required
              fullWidth
              margin="normal"
              label="Email Address"
              type="email"
              value={passkeyEmail}
              onChange={e => { setPasskeyEmail(e.target.value); setPasskeyEmailErr('') }}
              error={!!passkeyEmailErr}
              helperText={passkeyEmailErr}
              disabled={loading}
              InputLabelProps={{ shrink: !!passkeyEmail }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !passkeyEmail}
              sx={{ mt: 2, mb: 1.5, py: 1.2 }}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FingerprintIcon />}
            >
              {loading ? 'Authenticating…' : 'Login with passkey'}
            </Button>

            <Button
              fullWidth
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={closePasskeyView}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Back to sign in
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main login form (email / phone toggle)
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box className="Login_Page_Box">
      <Box
        className="Login_Box"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 420, p: 3 }}
      >
        {/* Logo */}
        <Box sx={{ mb: 1.5 }}>
          <img src="./logo-dark.png" alt="Rentalboard" width="80px" />
        </Box>

        <Typography component="h1" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          LOGIN TO YOUR ACCOUNT
        </Typography>

        {/* Email / Phone toggle */}
        <Box sx={{ display: 'flex', width: '100%', mb: 2, gap: 1 }}>
          <Button
            fullWidth
            variant={loginMethod === 'email' ? 'contained' : 'outlined'}
            onClick={() => switchMethod('email')}
            size="small"
            disabled={loading}
          >
            Email
          </Button>
          <Button
            fullWidth
            variant={loginMethod === 'phone' ? 'contained' : 'outlined'}
            onClick={() => switchMethod('phone')}
            size="small"
            disabled={loading}
          >
            Phone
          </Button>
        </Box>

        {/* Error alert */}
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 1.5 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {/* ── Email form ── */}
        {loginMethod === 'email' && (
          <Box component="form" onSubmit={handleEmailLogin} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailErr(validateEmail(e.target.value.trim()).msg) }}
              error={!!emailErr}
              helperText={emailErr}
              disabled={loading}
              InputLabelProps={{ shrink: !!email || emailAutofilled }}
              inputProps={{ onAnimationStart: onAutofill(setEmailAutofilled) }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: !!password || passwordAutofilled }}
              InputProps={{
                inputProps: { onAnimationStart: onAutofill(setPasswordAutofilled) },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(s => !s)} edge="end" size="small" tabIndex={-1}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label="Remember me"
              />
              <Button
                size="small"
                onClick={() => setForgotOpen(true)}
                sx={{ textTransform: 'none', color: '#d32f2f' }}
                disabled={loading}
              >
                Forgot password?
              </Button>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !email || !password}
              sx={{ mt: 2, mb: 1, py: 1.2 }}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </Box>
        )}

        {/* ── Phone form ── */}
        {loginMethod === 'phone' && (
          <Box component="form" onSubmit={handlePhoneLogin} noValidate sx={{ width: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter your phone number to receive a one-time code.
            </Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Phone Number"
              type="tel"
              autoFocus
              value={phone}
              onChange={e => setPhone(e.target.value)}
              disabled={loading}
              placeholder="555 000 0000"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !phone.trim()}
              sx={{ mt: 2, mb: 1, py: 1.2 }}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? 'Sending Code…' : 'Send Code'}
            </Button>
          </Box>
        )}
        {/* Passkey button — separate, matching DMS outline style */}
        {passkeyService.isSupported() && (
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={openPasskeyView}
            disabled={loading}
            startIcon={<FingerprintIcon />}
            sx={{ mb: 1.5, py: 1.1, textTransform: 'none' }}
          >
            Sign in with passkey
          </Button>
        )}
        {/* ── Secondary auth options ── */}
        <Divider sx={{ width: '100%', my: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, whiteSpace: 'nowrap' }}>
            or log in with
          </Typography>
        </Divider>

        {/* Microsoft SSO */}
        <Button
          fullWidth
          variant="outlined"
          onClick={() => { clearError(); loginWithMicrosoft() }}
          disabled={loading}
          startIcon={<MicrosoftIcon />}
          sx={{ py: 1.1, textTransform: 'none', borderColor: '#ccc', color: '#444' }}
        >
          Microsoft
        </Button>
      </Box>

      {/* MFA modal */}
      <MfaModal
        open={mfaOpen}
        userId={mfaUserId}
        mfaType={mfaType}
        rememberMe={rememberMe}
        onClose={() => setMfaOpen(false)}
        onSuccess={handleMfaSuccess}
      />

      {/* Forgot password modal */}
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </Box>
  )
}

export default LoginComponent
